import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DeleteConfirmationModal, useNotification } from '../common';

const InvoicesView = () => {
  // Get notification functions from context
  const { showNotification } = useNotification();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState({
    invoiceId: null,
    clientName: '',
    clientEmail: '',
    invoiceNumber: '',
    amount: '',
    currency: ''
  });
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  
  // Selection state
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        const response = await axios.get('/api/deals/invoices', config);
        setInvoices(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching invoices:', err);
        
        if (err.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (err.response.status === 404) {
            setError(err.response.data.message || 'Invoice feature is not yet available');
          } else {
            setError(`Server error (${err.response.status}): ${err.response.data.message || 'Unknown error'}`);
          }
        } else if (err.request) {
          // The request was made but no response was received
          setError('No response from server. Please check your connection.');
        } else {
          // Something happened in setting up the request that triggered an Error
          setError('Error preparing request: ' + err.message);
        }
        
        setLoading(false);
      }
    };
    
    fetchInvoices();
  }, [emailSent]);
  
  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    
    const formatOptions = {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    };
    
    return new Intl.NumberFormat('en-US', formatOptions).format(amount);
  };
  
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Handle invoice selection
  const handleInvoiceSelection = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
      setSelectAll(false);
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
      if (selectedInvoices.length + 1 === invoices.length) {
        setSelectAll(true);
      }
    }
  };
  
  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedInvoices([]);
      setSelectAll(false);
    } else {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
      setSelectAll(true);
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedInvoices.length === 0) return;
    
    // Set invoiceToDelete to null to indicate it's a bulk delete
    setInvoiceToDelete(null);
    setShowDeleteConfirm(true);
  };
  
  // Reset selections when invoices change
  useEffect(() => {
    setSelectedInvoices([]);
    setSelectAll(false);
  }, [invoices]);
  
  const handleGeneratePDF = (invoiceId) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // Instead of client-side navigation, make a server request for the PDF
    // and download it directly as a blob
    axios({
      url: `/api/deals/invoices/${invoiceId}/pdf`,
      method: 'GET',
      responseType: 'blob', // Important for handling binary data
      headers: {
        'x-auth-token': token
      }
    }).then((response) => {
      // Create a blob from the PDF stream
      const file = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a link and trigger download
      const fileURL = URL.createObjectURL(file);
      const fileLink = document.createElement('a');
      fileLink.href = fileURL;
      fileLink.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(fileLink);
      
      fileLink.click();
      
      // Clean up
      document.body.removeChild(fileLink);
      URL.revokeObjectURL(fileURL);
    }).catch(error => {
      console.error('Error downloading PDF:', error);
      showNotification('Failed to download PDF. Please try again.', 'error');
    });
  };
  
  const handleDeleteInvoice = (invoiceId) => {
    setInvoiceToDelete(invoiceId);
    setShowDeleteConfirm(true);
  };
  
  const executeDeleteInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const invoiceId = invoiceToDelete;
      
      // If bulk delete
      if (!invoiceId && selectedInvoices.length > 0) {
        // Delete invoices one by one
        for (const id of selectedInvoices) {
          await axios.delete('/api/deals/invoices/' + id, config);
        }
        
        // Remove the deleted invoices from state
        setInvoices(invoices.filter(invoice => !selectedInvoices.includes(invoice.id)));
        
        // Reset selection
        setSelectedInvoices([]);
        setSelectAll(false);
      } 
      // Single invoice delete
      else if (invoiceId) {
        await axios.delete('/api/deals/invoices/' + invoiceId, config);
        
        // Remove the deleted invoice from state
        setInvoices(invoices.filter(invoice => invoice.id !== invoiceId));
        
        // Show success notification
        showNotification('Invoice deleted successfully');
      }
      else if (selectedInvoices.length > 0) {
        // Show success notification for bulk delete
        showNotification(`${selectedInvoices.length} invoices deleted successfully`);
      }
            
      setInvoiceToDelete(null);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      
      let errorMessage = 'Failed to delete invoice';
      
      if (err.response) {
        // The request was made and the server responded with a status code
        errorMessage += `: ${err.response.data.message || err.response.statusText}`;
      } else if (err.request) {
        // The request was made but no response was received
        errorMessage += ': No response from server. Please check your connection.';
      } else {
        // Something else happened
        errorMessage += `: ${err.message}`;
      }
      
      showNotification(errorMessage, 'error');
    }
  };
  
  const handleSendEmail = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Get detailed invoice info including client email
      const response = await axios.get(
        '/api/deals/invoices/' + invoiceId, 
        config
      );
      
      const invoice = response.data;
      
      // Check if client email exists
      if (!invoice.client_email) {
        showNotification('Client email address not found. Please update the lead with an email address first.', 'error');
        return;
      }
      
      // Set data for the email modal
      setEmailModalData({
        invoiceId: invoice.id,
        clientName: invoice.client_name || 'Client',
        clientEmail: invoice.client_email,
        invoiceNumber: invoice.invoice_number,
        amount: formatCurrency(invoice.amount, invoice.currency),
        currency: invoice.currency
      });
      
      // Show the email modal
      setShowEmailModal(true);
      
      // Reset any previous email states
      setEmailSent(false);
      setEmailError(null);
      setSendingEmail(false);
      
    } catch (err) {
      console.error('Error preparing to send invoice email:', err);
      
      let errorMessage = 'Failed to prepare invoice email';
      
      if (err.response) {
        errorMessage += `: ${err.response.data.message || err.response.statusText}`;
      } else if (err.request) {
        errorMessage += ': No response from server. Please check your connection.';
      } else {
        errorMessage += `: ${err.message}`;
      }
      
      showNotification(errorMessage, 'error');
    }
  };
  
  const handleSendInvoiceEmail = async () => {
    try {
      setSendingEmail(true);
      setEmailError(null);
      
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Send the invoice via email
      await axios.post(
        '/api/deals/invoices/' + emailModalData.invoiceId + '/email',
        { message: emailMessage },
        config
      );
      
      // Mark as sent
      setEmailSent(true);
      
      // Reset the email message for next time
      setEmailMessage('');
      
      // Close the modal after a short delay
      setTimeout(() => {
        setShowEmailModal(false);
      }, 1500);
      
    } catch (err) {
      console.error('Error sending invoice by email:', err);
      
      let errorMessage = 'Failed to send invoice email';
      
      if (err.response) {
        errorMessage += `: ${err.response.data.message || err.response.statusText}`;
      } else if (err.request) {
        errorMessage += ': No response from server. Please check your connection.';
      } else {
        errorMessage += `: ${err.message}`;
      }
      
      setEmailError(errorMessage);
      
    } finally {
      setSendingEmail(false);
    }
  };
  
  if (loading) {
    return <div className="text-center py-8">Loading invoices...</div>;
  }
  
  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          executeDeleteInvoice();
          setShowDeleteConfirm(false);
        }}
        message={invoiceToDelete 
          ? "Are you sure you want to delete this invoice? This action cannot be undone."
          : `Are you sure you want to delete ${selectedInvoices.length} selected invoice${selectedInvoices.length !== 1 ? 's' : ''}? This action cannot be undone.`
        }
        title={invoiceToDelete ? "Delete Invoice" : "Delete Selected Invoices"}
      />
    
      <div className="mb-6">
        <h3 className="text-lg font-semibold">Invoices</h3>
      </div>
      
      {invoices.length === 0 ? (
        <div className="text-center py-8">
          <p>No invoices found.</p>
          <p className="text-gray-500 mt-2">Invoices are generated from the Deals section.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Bulk Actions Bar - only shown when invoices are selected */}
          {selectedInvoices.length > 0 && (
            <div className="bg-blue-50 border-b border-blue-200 p-2 mb-2 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-700">
                  {selectedInvoices.length} invoice{selectedInvoices.length !== 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={() => {
                    setSelectedInvoices([]);
                    setSelectAll(false);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleBulkDelete}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-2 py-3 w-8">
                  <div className="flex items-center">
                    <input
                      id="select-all-checkbox"
                      type="checkbox"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                    <label htmlFor="select-all-checkbox" className="sr-only">
                      Select all
                    </label>
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deal
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issue Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className={`hover:bg-gray-50 ${selectedInvoices.includes(invoice.id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <input
                        id={`invoice-checkbox-${invoice.id}`}
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => handleInvoiceSelection(invoice.id)}
                      />
                      <label htmlFor={`invoice-checkbox-${invoice.id}`} className="sr-only">
                        Select invoice
                      </label>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {invoice.invoice_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.client_name || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invoice.deal_title || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(invoice.amount, invoice.currency)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.issue_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(invoice.due_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      className="text-green-600 hover:text-green-900 mr-3 flex items-center"
                      onClick={() => handleGeneratePDF(invoice.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      PDF
                    </button>
                    <button 
                      className="text-blue-600 hover:text-blue-900 mr-3 flex items-center"
                      onClick={() => handleSendEmail(invoice.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Email
                    </button>
                    <button 
                      className="text-red-600 hover:text-red-900 flex items-center"
                      onClick={() => handleDeleteInvoice(invoice.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Email Invoice Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Send Invoice by Email
              </h3>
              <button 
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={sendingEmail}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {emailSent ? (
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Invoice successfully sent to {emailModalData.clientEmail}
              </div>
            ) : (
              <>
                {emailError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-4">
                    {emailError}
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">To:</p>
                      <p className="font-medium">{emailModalData.clientName} &lt;{emailModalData.clientEmail}&gt;</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Invoice:</p>
                      <p className="font-medium">{emailModalData.invoiceNumber} ({emailModalData.amount})</p>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="emailMessage" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Message (Optional)
                    </label>
                    <textarea
                      id="emailMessage"
                      rows="6"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter a custom message to send with the invoice, or leave blank to use the default template."
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      disabled={sendingEmail}
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      The PDF invoice will be automatically attached to the email.
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                    onClick={() => setShowEmailModal(false)}
                    disabled={sendingEmail}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center ${sendingEmail ? 'opacity-75 cursor-not-allowed' : ''}`}
                    onClick={handleSendInvoiceEmail}
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Send Invoice
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesView;